import React, { Component } from 'react';

import appSettings from '../../config/settings';
import * as log from '../../util/log';

import { LoadableComponentErrorBoundaryPage } from './LoadableComponentErrorBoundaryPage';

// Use ErrorBoyndary to catch ChunkLoadError
// https://reactjs.org/docs/error-boundaries.html
class LoadableComponentErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error, info) {
    // getDerivedStateFromError already stored the error for the fallback UI;
    // this side-effect-only hook exists purely to report it. Without it a
    // caught error renders a graceful apology but is invisible to monitoring —
    // worse than a white screen, because we'd never find out. ChunkLoadErrors
    // are reported too: they signal users stranded on a stale build.
    log.onRecoverableError(error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error) {
      // A ChunkLoadError means a stale client is asking for code chunks a new
      // deploy removed — "refresh to update" copy fits. Any OTHER error is a
      // genuine runtime throw: we must STILL render a fallback rather than
      // return children, because re-rendering the throwing child would re-throw
      // with no ancestor boundary to catch it and tear down the whole tree
      // (the white-screen incidents).
      const variant = error.name === 'ChunkLoadError' ? 'chunk' : 'generic';
      return <LoadableComponentErrorBoundaryPage variant={variant} />;
    }

    return this.props.children;
  }
}

// LoadableComponentErrorBoundary helps in situations
// where production build changes in the server and
// long-living client app tries to fetch code chunks that don't exist anymore.
// Note: in development mode with Hot Module Reloading (HMR) in use, this causes error loops.
const UseLoadableErrorBoundaryOnlyInProdutionMode = props => {
  const { children } = props;
  return appSettings.dev ? (
    children
  ) : (
    <LoadableComponentErrorBoundary>{children}</LoadableComponentErrorBoundary>
  );
};
export default UseLoadableErrorBoundaryOnlyInProdutionMode;
